import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { TriggerBalanceUpdate } from "../helpers/TriggerBalanceUpdate";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_API_URL } from "../helpers/Config";
import toast from "react-hot-toast";
import { FaCheckCircle, FaCopy, FaExchangeAlt, FaInfoCircle } from "react-icons/fa";
import { RxCrossCircled } from "react-icons/rx";
import { TbProgressHelp } from "react-icons/tb";
import moment from "moment";
import { QRCodeCanvas } from "qrcode.react";

const FundDeposit = ({ theme = "light" }) => {
    const MIN_DEPOSIT_USDT = 5;
    const [usdtAmount, setUsdtAmount] = useState("");
    const [activeTab, setActiveTab] = useState("bep20");
    const [transactionHash, setTransactionHash] = useState("");
    const [isProcessing, setIsProcessing] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [amountTouched, setAmountTouched] = useState(false);
    const [hashTouched, setHashTouched] = useState(false);
    const [recentDeposits, setRecentDeposits] = useState([]);
    const [usdtBalance, setUsdtBalance] = useState(0);

    const [paymentMethods, setPaymentMethods] = useState({
        bep20: {
            id: "bep20",
            name: "USDT (BEP-20)",
            description: "Binance Smart Chain",
            fee: "0%",
            minDeposit: `$${MIN_DEPOSIT_USDT} USDT`,
            depositAddress: "Loading...",
            network: "BEP20",
            confirmations: "3-15 minutes",
            color: "#f0b90b",
        },
        trc20: {
            id: "trc20",
            name: "USDT (TRC-20)",
            description: "Tether TRC-20 Network",
            fee: "0%",
            minDeposit: `$${MIN_DEPOSIT_USDT} USDT`,
            depositAddress: "Loading...",
            network: "TRC20",
            confirmations: "1-5 minutes",
            color: "#ff6b35",
        },
    });

    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const isDarkMode = theme === "dark";

    const quickAmounts = [10, 50, 100, 500];

    const FetchProfile = async () => {
        try {
            const res = await axios.post(`${BASE_API_URL}/account/profile`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
            });

            const data = res?.data;

            if (data?.error) {
                if (data?.error == "session expired") {
                    localStorage.clear("a5srf4wer7w1cd");
                    navigate("/login");
                } else {
                    toast.error(data?.error);
                }
            }

            if (data?.data) {
                const usdtBalance = data?.data?.usdt;
                const balance = data?.data?.balance;
                TriggerBalanceUpdate({ balance, usdt: usdtBalance });
                setUsdtBalance(usdtBalance);
            }
        } catch (error) {
            console.log("Failed to fetch profile");
        }
    };

    const FetchDepositAddress = async () => {
        try {
            setIsLoading(true);
            const response = await axios.post(`${BASE_API_URL}/payment/deposit-address`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
            });
            const data = response?.data;

            if (data?.error) {
                if (data?.error == "session expired") {
                    localStorage.clear("a5srf4wer7w1cd");
                    navigate("/login");
                } else {
                    toast.error(data?.error);
                }
            } else {
                setPaymentMethods({
                    trc20: {
                        ...paymentMethods.trc20,
                        depositAddress: data?.trc20 || "Address not available",
                    },
                    bep20: {
                        ...paymentMethods.bep20,
                        depositAddress: data?.bep20 || "Address not available",
                    },
                });
            }
        } catch (error) {
            console.error("Error fetching deposit address:", error);
            toast.error("Failed to load payment methods");
        } finally {
            setIsLoading(false);
        }
    };


    useEffect(() => {
        FetchProfile();
        FetchDepositAddress();
    }, []);

    const handleQuickAmount = (amount) => {
        setUsdtAmount(amount.toString());
        setAmountTouched(true);
    };

    const handleDepositClick = () => {
        setAmountTouched(true);
        setHashTouched(true);

        const amountNum = Number(usdtAmount);

        if (!usdtAmount || !Number.isFinite(amountNum) || amountNum < MIN_DEPOSIT_USDT) {
            return;
        }

        if (!transactionHash.trim()) {
            return;
        }

        setShowConfirmModal(true);
    };

    const handleConfirmDeposit = async () => {
        setIsProcessing(true);
        setShowConfirmModal(false);

        try {
            const res = await axios.post(`${BASE_API_URL}/payment/fund-deposit`, {
                amount: usdtAmount,
                ref_no: transactionHash,
                method: `usdt-${activeTab}`,
                token: localStorage.getItem("a5srf4wer7w1cd"),
            });

            const data = res?.data;

            if (data?.error) {
                if (data?.error == "session expired") {
                    localStorage.clear("a5srf4wer7w1cd");
                    navigate("/login");
                } else {
                    toast.error(data?.error);
                }
            }

            if (data?.msg) {
                toast.success(data?.msg);
                FetchProfile();
            }
        } catch (error) {
            console.log(error);
            toast.error("Failed to deposit USDT");
        } finally {
            setIsProcessing(false);
            setUsdtAmount("");
            setTransactionHash("");
            setAmountTouched(false);
            setHashTouched(false);
            FetchRecentDeposits();
        }
    };

    const GetStatusIcon = (status = "0") => {
        if (status == "0") {
            return <TbProgressHelp className="text-warning" />;
        } else if (status == "1") {
            return <FaCheckCircle className="text-success" />;
        } else if (status == "2") {
            return <RxCrossCircled className="text-danger" />;
        }
    };

    const FetchRecentDeposits = async () => {
        try {
            const res = await axios.post(`${BASE_API_URL}/payment/deposit-report`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
                last_id: 0,
                status: "all",
            });

            const data = res?.data;

            if (data?.error) {
                if (data?.error == "session expired") {
                    localStorage.clear("a5srf4wer7w1cd");
                    navigate("/login");
                } else {
                    toast.error(data?.error);
                }
            }

            if (data?.data) {
                setRecentDeposits(data?.data.slice(0, 3));
            }
        } catch (error) {
            console.log(error);
            toast.error("Failed to fetch recent deposits");
        }
    };

    useEffect(() => {
        FetchRecentDeposits();
    }, []);

    const selectedMethod = paymentMethods[activeTab];
    const amountError = amountTouched && (!usdtAmount || !Number.isFinite(Number(usdtAmount)) || Number(usdtAmount) < MIN_DEPOSIT_USDT);
    const hashError = hashTouched && !transactionHash.trim();

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { duration: 0.5, staggerChildren: 0.05 },
        },
    };

    const itemVariants = {
        hidden: { y: 15, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { duration: 0.4 },
        },
    };

    if (isLoading) {
        return (
            <div
                className="container-fluid px-3 py-4"
                style={{
                    maxWidth: "480px",
                    margin: "0 auto",
                    minHeight: "100vh",
                    background: isDarkMode ? "#0f172a" : "#f8fafc",
                    color: isDarkMode ? "#e5e7eb" : "#111827",
                }}
            >
                <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
                    <div className="text-center">
                        <div className="spinner-border text-primary mb-3" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="text-muted">Loading deposit interface...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="container-fluid px-3 py-3"
            style={{
                maxWidth: "480px",
                margin: "0 auto",
                minHeight: "100vh",
                background: isDarkMode ? "#0f172a" : "#f8fafc",
                color: isDarkMode ? "#e5e7eb" : "#111827",
            }}
        >
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h5 className="fw-bold mb-1" style={{ color: isDarkMode ? "#e5e7eb" : "#111827" }}>
                        Deposit USDT
                    </h5>
                    <p className="small text-muted mb-0">Add funds to your trading account</p>
                </div>
                <div className="text-end">
                    <div className="small text-muted">Available Balance</div>
                    <div className="fw-bold h5 mb-0" style={{ color: isDarkMode ? "#38bdf8" : "#3b82f6" }}>
                        ${parseFloat(usdtBalance).toLocaleString()}
                    </div>
                </div>
            </div>

            <motion.div variants={containerVariants} initial="hidden" animate="visible" className="d-flex flex-column gap-3">
                {/* Amount Input Card */}
                <motion.div
                    variants={itemVariants}
                    className="card border-0 shadow-sm"
                    style={{
                        background: isDarkMode ? "#1e293b" : "#ffffff",
                        borderRadius: "12px",
                    }}
                >
                    <div className="card-body p-3">
                        <label className="form-label small fw-semibold mb-3" style={{ color: isDarkMode ? "#94a3b8" : "#64748b" }}>
                            DEPOSIT AMOUNT (USDT)
                        </label>

                        <div className="input-group mb-3">
                            <span className="input-group-text bg-transparent border-end-0">
                                <span className="fw-bold">$</span>
                            </span>
                            <input
                                type="number"
                                className="form-control border-start-0 fw-bold fs-4"
                                placeholder="0.00"
                                value={usdtAmount}
                                onChange={(e) => {
                                    setUsdtAmount(e.target.value);
                                    setAmountTouched(true);
                                }}
                                style={{
                                    background: "transparent",
                                    color: isDarkMode ? "#e5e7eb" : "#111827",
                                    borderColor: isDarkMode ? "#334155" : "#e2e8f0",
                                }}
                            />
                        </div>

                        {amountError && (
                            <div className="alert alert-danger py-2 mb-3" style={{ fontSize: "0.8rem" }}>
                                <FaInfoCircle className="me-2" />
                                {!usdtAmount ? "Please enter amount" : `Minimum deposit is $${MIN_DEPOSIT_USDT}`}
                            </div>
                        )}

                        <div className="d-flex gap-2 flex-wrap">
                            {quickAmounts.map((amount) => (
                                <motion.button
                                    key={amount}
                                    className={`btn ${usdtAmount === amount.toString() ? "btn-primary" : "btn-outline-primary"} flex-grow-1`}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={() => handleQuickAmount(amount)}
                                    style={{ minWidth: "70px", fontSize: "0.85rem" }}
                                >
                                    ${amount}
                                </motion.button>
                            ))}
                        </div>
                    </div>
                </motion.div>

                {/* Network Selection */}
                <motion.div variants={itemVariants}>
                    <div className="card border-0 shadow-sm" style={{ background: isDarkMode ? "#1e293b" : "#ffffff", borderRadius: "12px" }}>
                        <div className="card-body p-3">
                            <label className="form-label small fw-semibold mb-3" style={{ color: isDarkMode ? "#94a3b8" : "#64748b" }}>
                                SELECT NETWORK
                            </label>

                            <div className="d-flex gap-2">
                                {Object.values(paymentMethods).map((method) => (
                                    <motion.button
                                        key={method.id}
                                        className={`btn ${activeTab === method.id ? '' : 'btn-outline-primary'} flex-grow-1`}
                                        onClick={() => setActiveTab(method.id)}
                                        style={{
                                            background: activeTab === method.id ? method.color : 'transparent',
                                            borderColor: method.color,
                                            color: activeTab === method.id ? 'white' : method.color,
                                            fontSize: "0.85rem",
                                            padding: "0.75rem",
                                        }}
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                    >
                                        <div className="d-flex align-items-center justify-content-center gap-2">
                                            <span>{method.network}</span>
                                        </div>
                                    </motion.button>
                                ))}
                            </div>

                        </div>
                    </div>
                </motion.div>

                {/* QR Code & Address Section - FIXED LAYOUT */}
                <motion.div variants={itemVariants}>
                    <div className="card border-0 shadow-sm" style={{ background: isDarkMode ? "#1e293b" : "#ffffff", borderRadius: "12px" }}>
                        <div className="card-body p-3">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <label className="form-label small fw-semibold mb-0" style={{ color: isDarkMode ? "#94a3b8" : "#64748b" }}>
                                    DEPOSIT ADDRESS
                                </label>

                                <div>
                                    <span className="fw-semibold">{selectedMethod.name}</span>
                                </div>
                            </div>


                            {/* QR Code - Centered */}
                            <div className="text-center mb-4">
                                <div className="d-inline-block p-3 bg-white rounded" style={{ border: "1px solid #e2e8f0" }}>
                                    <QRCodeCanvas
                                        value={selectedMethod.depositAddress}
                                        size={160}
                                        fgColor="#000000"
                                        bgColor="#FFFFFF"
                                        level="H"
                                    />
                                </div>
                                <div className="mt-2 small text-muted">Scan QR code or copy to deposit</div>
                            </div>

                            {/* Address Details - Below QR */}
                            <div className="mb-3">
                                <div className="input-group">
                                    <input
                                        type="text"
                                        className="form-control form-control-sm font-monospace text-center"
                                        value={selectedMethod.depositAddress}
                                        readOnly
                                        style={{
                                            fontSize: "0.75rem",
                                            background: isDarkMode ? "#0f172a" : "#f8fafc",
                                            borderColor: isDarkMode ? "#334155" : "#e2e8f0",
                                            color: isDarkMode ? "#cbd5e1" : "#475569",
                                            padding: "0.75rem",
                                        }}
                                    />
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => {
                                            navigator.clipboard.writeText(selectedMethod.depositAddress);
                                            toast.success("Address copied to clipboard");
                                        }}
                                        style={{
                                            background: selectedMethod.color,
                                            borderColor: selectedMethod.color,
                                            minWidth: "80px"
                                        }}
                                    >
                                        <FaCopy className="me-1" />
                                        Copy
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Transaction Hash Input */}
                <motion.div variants={itemVariants}>
                    <div className="card border-0 shadow-sm" style={{ background: isDarkMode ? "#1e293b" : "#ffffff", borderRadius: "12px" }}>
                        <div className="card-body p-3">
                            <label className="form-label small fw-semibold mb-3" style={{ color: isDarkMode ? "#94a3b8" : "#64748b" }}>
                                TRANSACTION HASH
                            </label>

                            <textarea
                                className={`form-control ${hashError ? "border-danger" : ""}`}
                                rows="3"
                                placeholder={`Paste your ${selectedMethod.network} transaction hash here...`}
                                value={transactionHash}
                                onChange={(e) => {
                                    setTransactionHash(e.target.value);
                                    setHashTouched(true);
                                }}
                                style={{
                                    background: isDarkMode ? "#0f172a" : "#f8fafc",
                                    color: isDarkMode ? "#e5e7eb" : "#111827",
                                    borderColor: isDarkMode ? "#334155" : "#e2e8f0",
                                    fontSize: "0.85rem",
                                }}
                            />

                            {hashError && (
                                <div className="text-danger small mt-2">
                                    Please enter transaction hash
                                </div>
                            )}

                            <div className="text-muted small mt-2">
                                Required to verify your deposit. Find this in your wallet's transaction history.
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Submit Button */}
                <motion.div variants={itemVariants}>
                    <motion.button
                        className="btn btn-primary w-100 py-3 fw-bold"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={handleDepositClick}
                        disabled={
                            isProcessing ||
                            !usdtAmount ||
                            !transactionHash ||
                            !Number.isFinite(Number(usdtAmount)) ||
                            Number(usdtAmount) < MIN_DEPOSIT_USDT ||
                            selectedMethod.depositAddress === "Loading..." ||
                            selectedMethod.depositAddress === "Address not available"
                        }
                        style={{
                            background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                            border: "none",
                            borderRadius: "12px",
                            fontSize: "1rem",
                        }}
                    >
                        {isProcessing ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Processing Deposit...
                            </>
                        ) : (
                            <>
                                <FaExchangeAlt className="me-2" />
                                {usdtAmount ? `Deposit $${parseFloat(usdtAmount).toLocaleString()} USDT` : "Confirm Deposit"}
                            </>
                        )}
                    </motion.button>
                </motion.div>

                {/* Recent Deposits */}
                <motion.div variants={itemVariants}>
                    <div className="card border-0 shadow-sm" style={{ background: isDarkMode ? "#1e293b" : "#ffffff", borderRadius: "12px" }}>
                        <div className="card-body p-3">
                            <h6 className="fw-bold mb-3" style={{ color: isDarkMode ? "#e5e7eb" : "#111827" }}>
                                Recent Deposits
                            </h6>

                            {recentDeposits.length > 0 ? (
                                <div className="list-group list-group-flush">
                                    {recentDeposits.map((item, index) => (
                                        <div key={index} className="list-group-item border-0 px-0 py-2" style={{ background: "transparent" }}>
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <div className="fw-medium" style={{ color: isDarkMode ? "#e5e7eb" : "#111827" }}>
                                                        ${parseFloat(item?.amount || 0).toLocaleString()}
                                                    </div>
                                                    <div className="small text-muted">
                                                        {moment(item?.date, "YYYY-MM-DD HH:mm:ss").format("MMM D, HH:mm")}
                                                    </div>
                                                </div>
                                                <div className="d-flex align-items-center gap-2">
                                                    <span className="badge" style={{
                                                        background: isDarkMode ? "#334155" : "#e2e8f0",
                                                        color: isDarkMode ? "#cbd5e1" : "#475569"
                                                    }}>
                                                        {item?.method}
                                                    </span>
                                                    {GetStatusIcon(item?.status)}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-4 text-muted">
                                    No recent deposits
                                </div>
                            )}

                            <button
                                className="btn btn-outline-primary w-100 mt-2"
                                onClick={() => navigate("/deposit-history")}
                                style={{ fontSize: "0.85rem" }}
                            >
                                View All Deposits
                            </button>
                        </div>
                    </div>
                </motion.div>

                {/* Information Panel */}
                <motion.div variants={itemVariants}>
                    <div className="card border-0 shadow-sm" style={{ background: isDarkMode ? "#1e293b" : "#ffffff", borderRadius: "12px" }}>
                        <div className="card-body p-3">
                            <h6 className="fw-bold mb-3 d-flex align-items-center" style={{ color: isDarkMode ? "#e5e7eb" : "#111827" }}>
                                <FaInfoCircle className="me-2" />
                                Deposit Information
                            </h6>

                            <div className="row small">
                                <div className="col-6 mb-2">
                                    <div className="text-muted">Minimum Deposit</div>
                                    <div className="fw-semibold">${MIN_DEPOSIT_USDT} USDT</div>
                                </div>
                                <div className="col-6 mb-2">
                                    <div className="text-muted">Processing Fee</div>
                                    <div className="fw-semibold text-success">0%</div>
                                </div>
                                <div className="col-6 mb-2">
                                    <div className="text-muted">Processing Time</div>
                                    <div className="fw-semibold">3 Confirmations</div>
                                </div>
                                <div className="col-6 mb-2">
                                    <div className="text-muted">Support</div>
                                    <div className="fw-semibold">24/7 Available</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </motion.div>

            {/* Confirmation Modal */}
            {showConfirmModal && (
                <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "400px" }}>
                        <div className="modal-content" style={{
                            background: isDarkMode ? "#1e293b" : "#ffffff",
                            borderRadius: "16px",
                            border: isDarkMode ? "1px solid #334155" : "none"
                        }}>
                            <div className="modal-header border-0 pb-0">
                                <h5 className="modal-title fw-bold">Confirm Deposit</h5>
                                <button type="button" className="btn-close" onClick={() => setShowConfirmModal(false)}></button>
                            </div>

                            <div className="modal-body py-4">
                                <div className="text-center mb-4">
                                    <div className="mb-3">
                                        <div className="display-6 fw-bold" style={{ color: isDarkMode ? "#38bdf8" : "#3b82f6" }}>
                                            ${parseFloat(usdtAmount).toLocaleString()}
                                        </div>
                                        <div className="text-muted">USDT via {selectedMethod.name}</div>
                                    </div>

                                    <div className="alert alert-warning small mb-0">
                                        Please verify all details before confirming
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <div className="small text-muted mb-2">Transaction Hash</div>
                                    <code className="d-block p-2 rounded" style={{
                                        background: isDarkMode ? "#0f172a" : "#f8fafc",
                                        fontSize: "0.8rem",
                                        wordBreak: "break-all"
                                    }}>
                                        {transactionHash}
                                    </code>
                                </div>
                            </div>

                            <div className="modal-footer border-0 pt-0">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowConfirmModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleConfirmDeposit}
                                >
                                    Confirm Deposit
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add CSS for spinner animation */}
            <style jsx>{`
                .spin {
                    animation: spin 1s linear infinite;
                }
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </motion.div>
    );
};

export default FundDeposit;